import type Phaser from 'phaser';
import { TILE_SIZE } from '../layout';

/**
 * Tall grass's blades (owner, 2026-10-04): long grass stands up off its tile and into the one
 * behind it, so it reads as tall at a glance. Drawn over the tile (which keeps a light meadow
 * base) like a prop, sorted against the mower by row. Frames: tall, then the stubble left by one
 * weak pass. Each frame is a tile wide and rises `BLADE_RISE` pixels above its tile.
 */
export const BLADES_KEY = 'blades';
export const BLADE_RISE = 10;
export const BLADE_W = TILE_SIZE;
export const BLADE_H = TILE_SIZE + BLADE_RISE;
/** Frames, left to right (append only: art sheets follow this layout). */
export const BLADE_FRAMES = ['tall', 'rough'] as const;
export type BladeFrame = (typeof BLADE_FRAMES)[number];

const TIP = '#f1f9b4';
const LIGHT = '#b8e878';
const MID = '#7cc74e';
const DARK = '#4f9a3c';
const ROOT = '#2f7a2e';
const CUT = '#dfeca0';

/** Clumps, back to front: x, base row (frame pixels), blade heights. Back ones stand higher. */
const TALL_CLUMPS: readonly [number, number, readonly number[]][] = [
  [2, 13, [11, 14, 10]],
  [9, 12, [13, 10, 15]],
  [13, 14, [12, 9]],
  [5, 17, [12, 15, 11]],
  [11, 18, [14, 11, 13]],
  [0, 21, [10, 13]],
  [7, 22, [13, 16, 12]],
  [14, 23, [11, 9]],
  [3, 25, [12, 10, 14]],
  [10, 25, [9, 12]],
];
/** Stubble after one weak pass: the same clumps, chopped short. */
const ROUGH_CLUMPS = TALL_CLUMPS.map(
  ([x, y, hs]) => [x, y, hs.map((h) => Math.max(3, Math.round(h * 0.35)))] as const,
);

type Put = (x: number, y: number, color: string) => void;

/** One clump: blades side by side, fanning a little, shaded root to tip; `lean` bends the tips. */
function clump(
  put: Put,
  x: number,
  base: number,
  heights: readonly number[],
  lean: number,
  cut: boolean,
): void {
  heights.forEach((h, k) => {
    const bx = x + k;
    const fan = k - (heights.length - 1) / 2; // outer blades splay outward
    for (let i = 0; i < h; i++) {
      const t = i / Math.max(1, h - 1); // 0 root .. 1 tip
      const bend = Math.round(fan * t * t * 1.5) + (t > 0.7 ? lean : 0);
      const c = cut
        ? i === h - 1
          ? CUT
          : t < 0.4
            ? ROOT
            : MID
        : i === h - 1
          ? TIP
          : t > 0.7
            ? LIGHT
            : t > 0.35
              ? MID
              : t > 0.12
                ? DARK
                : ROOT;
      put(bx + bend, base - i, c);
    }
  });
}

function paintFrame(
  ctx: CanvasRenderingContext2D,
  ox: number,
  frame: BladeFrame,
  lean: number,
): void {
  ctx.clearRect(ox, 0, BLADE_W, BLADE_H);
  const put: Put = (x, y, color) => {
    if (x < 0 || y < 0 || x >= BLADE_W || y >= BLADE_H) return;
    ctx.fillStyle = color;
    ctx.fillRect(ox + x, y, 1, 1);
  };
  const clumps = frame === 'tall' ? TALL_CLUMPS : ROUGH_CLUMPS;
  for (const [x, y, hs] of clumps) clump(put, x, y, hs, lean, frame === 'rough');
}

export function createBlades(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(BLADES_KEY, BLADE_W * BLADE_FRAMES.length, BLADE_H);
  if (!tex) throw new Error('Could not create blades texture');
  BLADE_FRAMES.forEach((f, i) => paintFrame(tex.context, i * BLADE_W, f, 0));
  tex.refresh();
  BLADE_FRAMES.forEach((_f, i) => tex.add(i, 0, i * BLADE_W, 0, BLADE_W, BLADE_H));
}

/** Repaints the blades upright or leaning (the breeze; one repaint sways every tuft). */
export function swayBlades(scene: Phaser.Scene, lean: Readonly<Record<BladeFrame, boolean>>): void {
  const tex = scene.textures.get(BLADES_KEY) as Partial<Phaser.Textures.CanvasTexture>;
  if (!tex.context || !tex.refresh) return;
  const ctx = tex.context;
  BLADE_FRAMES.forEach((f, i) => paintFrame(ctx, i * BLADE_W, f, lean[f] ? 1 : 0));
  tex.refresh.call(tex);
}
